/* eslint-disable no-undefined */
/* eslint-disable max-statements */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { Form, Formik, FormikProps, FormikConfig } from "formik";
import { gql } from "graphql-tag";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, MutationFunction } from "@apollo/client";
import { Grid } from "@mui/material";
import { omit } from "lodash";
import { actionFooterStyles, BackButton, Button, Modal } from "components/haven-design";
import { useSnackbar } from "components/haven-design/snackbar";
import MultiStepProgress from "components/multi-step-progress";
import { NullableAddress, ValidationResult, getAddress, validateStreetAddress } from "services/validate-street-address";
import { getQueryParam } from "utils/url";
import { MainLayout } from "pages/layouts";
import logger from "services/logger";
import { DialogContent } from "components/haven-design/modal/dialog";
import { Address, UpdateRequest } from "../../types";
import { contactStepSchema, incomeStepSchema } from "../schema";
import { FormProps, FormValues } from "../types";
import { convertAndFormatMoney, interestIsRemoved, moneyToFloat } from "../utils";
import ViewAddress from "./address";
import FormContact from "./form-contact";
import FormHousehold from "./form-household";
import FormReview from "./form-review";
import FormWaitlist from "./form-waitlist";
import SuccessView from "./success";
import styles from "./update.module.css";
import addressStyles from "./verify-address.module.css";

const MUTATE_UPDATE_REQUEST = gql`
  mutation SubmitUpdateRequest($code: String!, $data: FormUpdate!) {
    submitWaitlistUpdate(code: $code, data: $data) {
      code
    }
  }
`;

const QUERY_UPDATE_REQUEST = gql`
  query UpdateRequest($code: String!) {
    updateRequest(code: $code) {
      code
      isActive
      isComplete
      interests {
        id
        userId
        status {
          name
          changedAt
        }
        householdMember0FirstName
        householdMember0LastName
        interestListName
        interestList {
          email
          name
          address {
            city
            state
          }
          waitTime
        }
      }
    }
  }
`;

type QueryData = {
  updateRequest: UpdateRequest;
}

type QueryVars = {
  code: string;
}

type MutationData = UpdateRequest

type ResponseChannel = "EMAIL" | "SMS" | "LETTER";

type MutationVars = {
  code: string;
  data: FormValues & { responseChannel: ResponseChannel };
}

const initialFormValues: FormValues = {
  isStayingOnList: undefined,
  householdNetIncome: undefined,
  householdIncome: undefined,
  householdIncomeFrequency: undefined,
  householdCount: undefined,
  householdHasAuthorityVoucher: undefined,
  address1: undefined,
  address2: undefined,
  city: undefined,
  state: undefined,
  zip: undefined,
  email: undefined,
  primaryPhone: undefined,
  primaryPhoneType: undefined,
  secondaryPhone: undefined,
  secondaryPhoneType: undefined,
  contactRelation: undefined,
  contactName: undefined,
  contactPhone: undefined,
  contactPhoneType: undefined,
  contactEmail: undefined,
};

type Step = {
  key: string;
  title: string;
  component: React.FC<FormProps>;
  schema?: typeof incomeStepSchema | typeof contactStepSchema;
}

type SubmitForm = (values: FormValues) => ReturnType<MutationFunction<MutationData, MutationVars>>;

const steps: Step[] = [
  {
    key: "waitlist",
    title: "WAITLIST UPDATE",
    component: FormWaitlist,
  },
  {
    key: "household",
    title: "UPDATE YOUR HOUSEHOLD INFORMATION",
    component: FormHousehold,
    schema: incomeStepSchema,
  },
  {
    key: "contact",
    title: "UPDATE YOUR CONTACT INFORMATION",
    component: FormContact,
    schema: contactStepSchema,
  },
  { key: "review", title: "CONFIRM YOUR INFORMATION", component: FormReview },
];

type Props = {};

const parseChannel = () => {
  switch (getQueryParam("c")) {
    case "e":
      return "EMAIL";
    case "s":
      return "SMS";
    default:
      return "LETTER";
  }
};

const getCurrentStep = (currentStep: number): Step | null => {
  if (currentStep in steps) return steps[currentStep];

  return null;
};

const isMatchingAddress = (original: Address, suggested: Address) =>
  original.address1.toLowerCase().trim() === suggested.address1.toLowerCase().trim() &&
  original.city.toLowerCase().trim() === suggested.city.toLowerCase().trim() &&
  original.state.toLowerCase().trim() === suggested.state.toLowerCase().trim() &&
  original.zip.toLowerCase().trim().slice(0, 5) ===
    suggested.zip.slice(0, 5).toLowerCase().trim();

const castFormValues = (formValues: FormValues): FormValues => {
  const castedValues = incomeStepSchema.concat(contactStepSchema).cast(formValues);

  return {
    ...omit(castedValues, ["householdIncomeFrequency", "householdIncome"]),
    householdNetIncome: moneyToFloat(castedValues.householdNetIncome ?? "0"),
  };
};


const getFormTitle = ({ interests }: UpdateRequest) => {
  if (interests.length === 1) {
    return `${interests[0].interestListName} Application`;
  }

  return "Haven Connect Update";
};

const SuggestedAddress: React.FC<{
  formProps: FormikProps<FormValues>,
  showValidationModal: boolean,
  setShowValidationModal: React.Dispatch<React.SetStateAction<boolean>>,
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>,
  suggestedAddress: Address,

}> = ({ formProps,
  showValidationModal, setShowValidationModal,
  setCurrentStep, suggestedAddress }) => {
  const { values } = formProps;
  const close = () => setShowValidationModal(false);
  const next = () => {
    setShowValidationModal(false);
    setCurrentStep((currStep) => currStep + 1);
  };

  return (
    <Modal isOpen={showValidationModal} title="Verify Address" onRequestClose={close} style={{ overflow: "scroll" }}>
      <DialogContent>
        <p className={addressStyles.disclaimer}>
          The Postal Service suggests the following changes to your address. Use the address
          recommended by the Postal Service, or what you wrote?
        </p>

        <div style={{ margin: 8 }}>
          <Grid container size={{ xs: 12 }} spacing={2}>
            <Grid size={{ xs: 12, md: 6 }} className={styles.suggestedColumn}>
              <ViewAddress heading="US Postal Recommended Address" address={suggestedAddress} />
              <Button
                color="primary"
                size="S"
                disabled={formProps.isSubmitting}
                onClick={() => {
                  formProps.setValues({ ...formProps.values, ...suggestedAddress });
                  next();
                }}
              >
                Use Recommended Address
              </Button>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <ViewAddress heading="Original Address" address={getAddress(values as NullableAddress)} />
              <Button color="secondary" size="S" disabled={formProps.isSubmitting} onClick={next}>
                Use Original
              </Button>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
    </Modal>
  );
};


const UpdateFormComponent: React.FC<{
  currentStep: number,
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>,
  removedInterests: string[],
  setRemovedInterests: React.Dispatch<React.SetStateAction<string[]>>,
  showValidationModal: boolean,
  setShowValidationModal: React.Dispatch<React.SetStateAction<boolean>>,
  suggestedAddress: Address,
  request: UpdateRequest,
  formProps: FormikProps<FormValues>}> =
  ({
    request, formProps,
    currentStep, setCurrentStep,
    removedInterests, setRemovedInterests,
    showValidationModal, setShowValidationModal,
    suggestedAddress,
  }) => {

    const isLastStep = currentStep === steps.length - 1;

    const onPrevForm = ({ setTouched }: FormikProps<FormValues>) => {
      setTouched({});
      setCurrentStep((currStep: number) => currStep - 1);
    };

    const step = getCurrentStep(currentStep);
    if (!step) return null;
    const allListsRemoved = request.interests
      .every(i => interestIsRemoved(i.id, i.status.name, removedInterests));

    const handleSetRemoved = (interest: string) => {
      setRemovedInterests((current) => [interest, ...current]);
    };
    const StepFC = step.component;

    return (
      <Form>
        <StepFC data={request} setRemoved={handleSetRemoved} removedInterests={removedInterests} />

        <div className={`${actionFooterStyles.container} margin-top-l`}>
          {currentStep > 0 && (
            <BackButton size="L" color="secondary" onClick={() => onPrevForm(formProps)} />
          )}

          <div className={actionFooterStyles.spacer} />
          <Button type="submit" color="primary" size="L" disabled={formProps.isSubmitting || allListsRemoved}>
            {isLastStep ? "Submit" : "Next"}
          </Button>
        </div>
        <SuggestedAddress
          formProps={formProps}
          setCurrentStep={setCurrentStep}
          showValidationModal={showValidationModal}
          setShowValidationModal={setShowValidationModal}
          suggestedAddress={suggestedAddress} />
      </Form>
    );
  };

const defaultAddress = {
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
};

const UpdateInfo: React.FC<Props> = () => {
  const { code = "" } = useParams<{ code: string }>();
  const [currentStep, setCurrentStep] = useState(0);
  const [success, setSuccess] = useState(false);
  const [suggestedAddress, setSuggestedAddress] = useState<Address>(defaultAddress);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [removedInterests, setRemovedInterests] = useState<string[]>([]);
  const result = useQuery<QueryData, QueryVars>(QUERY_UPDATE_REQUEST, { variables: { code } });
  const [submit] = useMutation<MutationData, MutationVars>(MUTATE_UPDATE_REQUEST);

  const { openSnackbar } = useSnackbar();

  const onSubmit = (onSubmitForm: SubmitForm): FormikConfig<FormValues>["onSubmit"] => async (values, { setTouched, setSubmitting, setValues, setFieldValue }) => {
    const step = getCurrentStep(currentStep);
    if (!step) return;

    const next = () => {
      setTouched({});
      setCurrentStep((currStep) => currStep + 1);
    };

    setSubmitting(false);
    switch (step.key) {
      case "waitlist":
      case "household":
        setFieldValue("householdNetIncome", convertAndFormatMoney(values.householdIncome ?? "0", values.householdIncomeFrequency as ("monthly" | "yearly") || "yearly"));
        next();
        return;

      case "contact": {
        const address = getAddress(values as NullableAddress);
        if (!address.address1 && !address.city && !address.state) {
          next();
          return;
        }

        setSubmitting(true);
        await validateStreetAddress(address)
          .then((response: ValidationResult) => {
            setSubmitting(false);

            if (!response.wasFound) {
              openSnackbar({ message: "Address hasn't been found in USPS", status: "alert", duration: 5000 });
              next();
              return;
            }

            if (response.isMissingSecondary) {
              openSnackbar({
                message: "The USPS has indicated that the address entered requires a " +
                "secondary address to be deliverable. Please consider " +
                "adding this if you have an apartment number.",
                status: "alert",
                duration: 5000,
              });
            }

            if (isMatchingAddress(address, response.suggestedAddress)) {
              setValues({ ...values, ...response.suggestedAddress });
              next();
              return;
            }

            setShowValidationModal(true);
            setSuggestedAddress(response.suggestedAddress);
          })
          .catch(() => {
            setSubmitting(false);
            next();
          });
        return;
      }

      case "review":
        setSubmitting(true);
        onSubmitForm(castFormValues(values))
          .then(() => {
            setSuccess(true);
          })
          .catch((err) => {
            logger.sendError(err);
          })
          .finally(() => {
            setSubmitting(false);
          });
        break;
      default:

    }
  };

  if (
    result.loading ||
      result.error ||
      !result.data ||
      result.data.updateRequest.interests.length === 0
  ) {
    return null;
  }
  const {
    data: {
      updateRequest: request,
      updateRequest: { interests },
    },
  } = result;

  const remainingInterests = interests
    .filter(i => !interestIsRemoved(i.id, i.status.name, removedInterests));
  const removedInterestLists = interests
    .filter(i => interestIsRemoved(i.id, i.status.name, removedInterests));
  const allRemoved = remainingInterests.length === 0;

  if (!request.isActive) {
    return (
      <div className="card">
        <p className={styles.largePara}>
          The deadline has passed for this update.
          If you&apos;ve received a removal notice,
          you&apos;ll need to contact the property manager directly. Thanks!
        </p>
      </div>
    );
  }

  if ((request.isComplete && !allRemoved) || success) {
    return (
      <SuccessView
        firstName={interests[0].householdMember0FirstName || ""}
        lastName={interests[0].householdMember0LastName || ""}
        listNames={[...new Set(remainingInterests.map(interest => interest.interestList.name ?? ""))]}
        removedListNames={[...new Set(removedInterestLists.map(interest => interest.interestList.name ?? ""))]}
        hasAccount={!!interests.map((i) => i.userId).filter((id) => id !== null).length}
        requestCode={code}
      />
    );
  }

  const step = getCurrentStep(currentStep);
  if (!step) return null;

  return (
    <MainLayout backgroundColor="gray" showHeader={false} showFooter={false}>
      <div className={styles.container}>
        <h1 className="margin-bottom-m">{getFormTitle(request)}</h1>
        <div className="card">
          <MultiStepProgress
            currentStepNumber={currentStep}
            className="margin-bottom-m"
            stepTitles={steps.map(({ title }) => title)}
          />
          <Formik<FormValues>
            initialValues={initialFormValues}
            validationSchema={step.schema}
            onSubmit={onSubmit((values) =>
              submit({
                variables: {
                  code,
                  data: { ...values, responseChannel: parseChannel() },
                },
              }))}
          >
            {(formProps) =>
              <UpdateFormComponent
                request={request}
                formProps={formProps}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                removedInterests={removedInterests}
                setRemovedInterests={setRemovedInterests}
                showValidationModal={showValidationModal}
                setShowValidationModal={setShowValidationModal}
                suggestedAddress={suggestedAddress}
              />}
          </Formik>
        </div>
      </div>
    </MainLayout>
  );
};

export default UpdateInfo;
